import { RouteLoaderData } from '@/app/routes/__root'
import ImageComparison, {
  ViewMode,
} from '@/components/features/maps/CompareAssetSets/ImageComparison'
import ViewModeToggle from '@/components/features/maps/CompareAssetSets/ImageComparison/ViewModeToggle'
import MapCompare from '@/components/features/maps/CompareAssetSets/Map'
import PageHeader from '@/components/layout/PageHeader'
import NotFound from '@/components/utils/NotFound'
import RouteLoadFailed from '@/components/utils/RouteLoadFailed'
import { useLayoutControls } from '@/hooks/useLayoutControls'
import { throwLoaderError } from '@/lib/loader-errors'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { z } from 'zod'

import {
  AssetSet,
  AssetWithPresignedUrl,
} from '@droidmap/asset-service-contract'
import { DroidMapMetadata } from '@droidmap/shared-service-contract'

const SearchParamsSchema = z
  .object({
    variant: z
      .enum(['overlay', 'side-by-side', 'compare'])
      .catch('side-by-side')
      .default('side-by-side'),
    assetIds: z.string().optional(),
  })
  .catch({
    variant: 'side-by-side',
    assetIds: undefined,
  })

export const Route = createFileRoute(
  '/_authenticated/orgs/$orgId_/projects/$projectId_/_pathless/compare_/$compareType',
)({
  validateSearch: SearchParamsSchema,
  loader: async ({
    params,
    context,
    location,
  }): Promise<
    RouteLoaderData & {
      compareType: string
      assetSets?: AssetSet[]
      assets?: AssetWithPresignedUrl[]
    }
  > => {
    const { compareType, orgId, projectId } = params
    const { assetClient } = context.client
    const { metadata } = context

    // Get assetIds from URL search params
    const searchParams = new URLSearchParams(location?.search || '')
    const assetIds = searchParams.get('assetIds')

    if (!assetIds || typeof assetIds !== 'string') {
      // Redirect to compare selection page if no assetIds provided
      throw redirect({
        to: '/orgs/$orgId/projects/$projectId/compare',
        params: { orgId, projectId },
      })
    }

    const assetIdArray = assetIds.split(',').filter((id) => id.trim())
    if (assetIdArray.length < 2) {
      // Same remedy as the `!assetIds` branch above: a comparison needs two
      // sides, and a link that carries fewer is malformed — a stale bookmark
      // or a hand-edited URL. Sending them back to the selection page beats
      // an error screen, which since #2658 gave this route a shared
      // `errorComponent` would read "Something went wrong on our side …  try
      // again in a moment": a server blamed for a bad link, offering a retry
      // that can never succeed.
      throw redirect({
        to: '/orgs/$orgId/projects/$projectId/compare',
        params: { orgId, projectId },
      })
    }

    // For drone-images and image-comparison, the IDs are asset set IDs, not asset IDs
    try {
      if (compareType === 'image-comparison') {
        const assetSets = await Promise.all(
          assetIdArray.map((assetId) =>
            assetClient.getAssetSet({ assetSetId: assetId }, metadata),
          ),
        )

        return {
          crumb: 'Compare',
          compareType,
          assetSets,
          metadata,
          layout: {
            variant: 'fullWidth' as const,
          },
        }
      } else {
        // For other comparison types, the IDs are asset IDs
        const assets = await Promise.all(
          assetIdArray.map((assetId) =>
            assetClient.getAsset({ assetId }, metadata),
          ),
        )

        return {
          crumb: 'Compare',
          compareType,
          assets: assets as AssetWithPresignedUrl[],
          metadata,
          layout: {
            variant: 'fullWidth' as const,
          },
        }
      }
    } catch (error) {
      // Any one of the assets being genuinely absent (404/403) still means
      // "not found"; a 5xx on one of the parallel fetches does not, and used
      // to be reported as a missing comparison anyway (#2658).
      throwLoaderError(error, 'comparison assets')
    }
  },

  component: Compare,
  notFoundComponent: () => (
    <NotFound
      resource="Comparison Assets"
      message="One or more assets in this comparison couldn't be found or you don't have permission to access them."
    />
  ),
  errorComponent: () => <RouteLoadFailed resource="comparison" />,
})

function Compare() {
  const { compareType, projectId, orgId } = Route.useParams()
  const search = Route.useSearch()
  const loaderData = Route.useLoaderData()
  const navigate = useNavigate()
  const [viewMode, setViewMode] = useState<ViewMode>('asset-sets')

  // Only show controls for image comparison, not map comparison
  const isImageComparison = compareType === 'image-comparison'

  // Create the controls component (memoized to prevent infinite re-renders)
  const controls = useMemo(
    () =>
      isImageComparison ? (
        <ViewModeToggle viewMode={viewMode} onViewModeChange={setViewMode} />
      ) : null,
    [viewMode, isImageComparison],
  )

  // Use the layout controls hook to determine where to show controls
  const { isFullWidth } = useLayoutControls(controls)

  // Get validated search params
  const { assetIds, variant = 'side-by-side' } = search

  if (!assetIds || typeof assetIds !== 'string') {
    throw new Error('Asset IDs not provided in search params')
  }

  const assetIdArray = assetIds.split(',').filter((id) => id.trim())
  if (assetIdArray.length < 2) {
    throw new Error('At least 2 asset IDs are required')
  }

  // For drone-images and image-comparison, we need asset sets with assets, not individual assets
  const isAssetSetComparison = compareType === 'image-comparison'

  if (isAssetSetComparison) {
    const { assetSets, metadata } = loaderData as {
      assetSets: AssetSet[]
      metadata: DroidMapMetadata
      layout: { variant: 'fullWidth' }
    }
    const assetSetNames = assetSets.map((as) => as.name).join(' vs. ')
    const subTitle = `Asset Sets: ${assetSetNames}`

    const title = 'Compare Images'

    const handleAssetSetChange = (assetSetId: string, index: number) => {
      const newAssetIds = [...assetIdArray]
      newAssetIds[index] = assetSetId

      navigate({
        to: '/orgs/$orgId/projects/$projectId/compare/$compareType',
        params: {
          orgId,
          projectId,
          compareType,
        },
        search: {
          variant,
          assetIds: newAssetIds.join(','),
        },
      })
    }

    return (
      <div className="flex flex-col h-full">
        {!isFullWidth && (
          <PageHeader title={title} subtitle={subTitle}>
            {controls}
          </PageHeader>
        )}
        <div className="flex-1">
          <ImageComparison
            assetSets={assetSets}
            variant={variant}
            metadata={metadata}
            projectId={projectId}
            onAssetSetChange={handleAssetSetChange}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
        </div>
      </div>
    )
  } else {
    const { assets, metadata } = loaderData as {
      assets: AssetWithPresignedUrl[]
      metadata: DroidMapMetadata
      layout: { variant: 'fullWidth' }
    }
    const assetNames = assets.map((a) => a.name).join(' vs. ')
    const subTitle = `Assets: ${assetNames}`

    if (compareType !== 'map') {
      throw new Error(`Unsupported comparison type: ${compareType}`)
    }

    return (
      <div className="flex flex-col h-full">
        <PageHeader title="Compare" subtitle={subTitle} hidden />
        <div className="flex-1">
          <MapCompare
            left={assets[0]}
            right={assets[1]}
            variant={variant}
            metadata={metadata}
          />
        </div>
      </div>
    )
  }
}
